import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BellRing, Save } from "lucide-react";

/**
 * R127 — Resident notification preferences.
 *
 * Right now: opt in/out of the weekly forum digest. Structured so future
 * toggles (maintenance updates, delinquency nudges, etc.) drop in without
 * refactoring the schema.
 */
export default function NotificationSettingsPage() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/notification-prefs");
      setPrefs(data);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/notification-prefs", {
        forum_digest_optout: !!prefs.forum_digest_optout,
      });
      setPrefs(data);
      toast.success("Notification preferences saved");
    } catch (e) { toast.error(apiErrorMessage(e) || "Save failed"); }
    setSaving(false);
  };

  if (!prefs) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 space-y-6 max-w-2xl" data-testid="notification-settings-page">
      <PageHeader
        eyebrow="Preferences"
        title="Notification Settings"
        subtitle="Choose which emails you want from your community. You'll always get transactional messages like payment receipts and maintenance updates."
      />

      <Card className="p-6 border-white/10 bg-white/[0.02] space-y-5"
             data-testid="notif-forum-digest-card">
        <div className="flex items-start gap-4">
          <div className="rounded-full bg-orange-500/10 p-2 shrink-0">
            <BellRing className="h-4 w-4 text-orange-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <div className="text-sm font-medium">Weekly community forum digest</div>
              <Switch
                checked={!prefs.forum_digest_optout}
                onCheckedChange={(v) => setPrefs({ ...prefs, forum_digest_optout: !v })}
                data-testid="forum-digest-toggle"
              />
            </div>
            <div className="text-xs text-zinc-400 mt-2 leading-relaxed">
              A quick Monday-morning recap of the top-liked forum threads and any
              pinned announcements from your community board. Includes a friendly
              two-sentence AI summary of the week.
            </div>
            <div className="text-[11px] text-zinc-500 mt-2">
              {prefs.forum_digest_optout
                ? "You're opted out — no weekly digest will be sent to your inbox."
                : "You're subscribed — you'll receive the digest each Monday."}
            </div>
          </div>
        </div>
      </Card>

      <div>
        <Button onClick={save} disabled={saving} className="gap-1.5"
                data-testid="save-prefs-btn">
          <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save preferences"}
        </Button>
      </div>
    </div>
  );
}
